import axios, {
  AxiosAdapter,
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  getAdapter,
  InternalAxiosRequestConfig,
} from "axios";
import axiosRetry from "axios-retry";
import { validateString } from "./helpers/ObjectCheckerHelper";
import { API_CALL_NOTIFICATION } from "./constants/notification";
import { formatMessage, formatMessageWithParams, tryFormatMessageWithJson } from "./helpers/LanguageHelper";
import NotificationManager from "../common/notification/NotificationManager";
import LoadingManager from "../common/loading/LoadingManager";
import { handleSSOErrors } from "./helpers/SSOErrorsHandler";
import { RootState, store } from "../redux/store/store";
import {
  selectActiveConfiguration,
  selectBackendUrl,
  updateAuthDataForTenantConf,
} from "../redux/feature/settings/configurations-slice";
import { Buffer } from "buffer";
import { refreshToken } from "../common/login/RefreshToken";
import { AuthOutcome } from "../common/login/DialogCommons";
import { AxiosCustomParams } from "./helpers/axios.helpers";
import StandaloneModalManager from "../common/standaloneModal/StandaloneModalManager";
import { setHasElaboratedAtLeastOnce } from "../redux/feature/object/remote-elaborations-slice";
import coCache from "../lib/CoCache/index";

const API_CALL_KEY = API_CALL_NOTIFICATION;

const defaultOptions: AxiosRequestConfig = {
  headers: {
    "Content-Type": "application/json",
  },
};
export const axiosInstance = axios.create(defaultOptions);

export function getBackendUrl(): string {
  return selectBackendUrl(store.getState());
}

axiosRetry(axiosInstance, {
  retries: 2,
  retryDelay: (retryCount) => (retryCount === 1 ? 0 : 1000),
  retryCondition: axiosRetry.isNetworkError,
  onRetry: (retryCount, error, requestConfig) => {
    logAxiosError(error);
    console.warn(
      `Retry #${retryCount} for ${requestConfig.method?.toUpperCase()} ${requestConfig.url} — ${error.message}`
    );
  },
});

const azureAccessTokenWrapper: {
  token?: Promise<string>;
  acquiredTimestamp?: number;
} = {};

const bearerTokenLock: {
  lock?: Promise<AuthOutcome>;
} = {};

axiosInstance.interceptors.request.use(async function (config) {
  const state: RootState = store.getState();

  const params: AxiosCustomParams | undefined = config.params;

  if (!params?.notShowLoading) {
    LoadingManager.create(API_CALL_KEY + config.url);
  }
  if (params?.generatesElaboration) {
    store.dispatch(setHasElaboratedAtLeastOnce());
  }

  const backend_url = selectBackendUrl(state);
  if (validateString(backend_url)) {
    config.baseURL = backend_url;
  }

  try {
    config.headers = { ...config.headers, ...(await getHeaders()) };
  } catch (e) {
    return Promise.reject(new AxiosError(e.message, e.code, config));
  }

  console.log("calling api " + config.url);

  return config;
});

export async function getHeaders(withMsToken = true): Promise<any> {
  const state = store.getState();
  const selectedConf = selectActiveConfiguration(state);
  const tenant_id = selectedConf.tenantId;

  const headers: any = {};

  if (validateString(tenant_id)) {
    headers["X-Tenant-Id"] = tenant_id;
  }

  headers.Authorization = await getAuthorizationHeader(selectedConf, tenant_id);

  if (withMsToken) {
    headers.Azure_AccessToken = await getAzureAccessTokenHeader();
  }

  return headers;
}

// Resolves the bearer token for the current tenant: returns the cached token while it is still
// valid, otherwise refreshes it first (de-duplicating concurrent refreshes via bearerTokenLock).
async function getAuthorizationHeader(
  selectedConf: ReturnType<typeof selectActiveConfiguration>,
  tenant_id: string
): Promise<string> {
  let tokenIsPresent = false;
  let tokenIsValid = false;
  if (validateString(selectedConf?.authData?.access_token)) {
    tokenIsPresent = true;
    tokenIsValid = Date.now() < selectedConf.authData.expires_at * 1000;
  }

  if (tokenIsValid) {
    return `Bearer ${selectedConf.authData.access_token}`;
  }

  // we haven't a valid access token we start login process
  console.log("token not valid we try to refresh");
  if (bearerTokenLock.lock == null) {
    //checks if bearerTokenLock.lock is a Promise
    bearerTokenLock.lock = refreshToken(tenant_id);
  }
  try {
    const loginOutcome = await bearerTokenLock.lock;
    console.log("refresh token successful");
    return `Bearer ${loginOutcome.authData.access_token}`;
  } catch (e) {
    console.error("error trying to refresh token", e);
    store.dispatch(updateAuthDataForTenantConf({ authData: undefined, tenantId: tenant_id }));
    throw new AuthError(tokenIsPresent ? e.toString() : "NO_TOKEN", "401");
  } finally {
    bearerTokenLock.lock = undefined;
  }
}

// Resolves the Azure SSO access token header. Errors are handled internally (logged and routed
// to handleSSOErrors) so a failure to get an SSO token never fails the whole request.
async function getAzureAccessTokenHeader(): Promise<string | undefined> {
  try {
    if (
      !azureAccessTokenWrapper.acquiredTimestamp ||
      azureAccessTokenWrapper.acquiredTimestamp + 60000 < new Date().getTime()
    ) {
      console.log("trying to get office sso access token ...");
      azureAccessTokenWrapper.token = Office.auth.getAccessToken({
        allowSignInPrompt: true,
        allowConsentPrompt: true,
      });
      azureAccessTokenWrapper.acquiredTimestamp = new Date().getTime();
    }

    return await azureAccessTokenWrapper.token;
  } catch (e) {
    console.error("error trying to get token:" + e.message, e);
    handleSSOErrors(e);
    return undefined;
  }
}

function logAxiosError(err: AxiosError) {
  // Cast needed: Azure_AccessToken is a custom header this add-in sends, not one of the
  // well-known headers axios's own AxiosRequestHeaders type models.
  const {
    Authorization: _auth,
    Azure_AccessToken: _azure,
    ...safeErrHeaders
  } = (err.config?.headers ?? {}) as Record<string, unknown>;
  console.error(
    "Axios Error details:",
    JSON.stringify({
      message: err.message,
      code: err.code,
      status: err.response?.status,
      data: err.response?.data,
      config: {
        ...err.config,
        headers: {
          ...safeErrHeaders,
          Authorization: _auth ? "[PRESENT]" : "[ABSENT]",
          Azure_AccessToken: _azure ? "[PRESENT]" : "[ABSENT]",
        },
      },
      stack: err.stack,
    })
  );
}

// Whether the interceptor should surface a notification for this error by default; several
// status-specific branches below still override the result (e.g. 413/555/503 force it off, 409
// derives it from the response payload).
function getDefaultNotifyError(err): boolean {
  return (
    !err.config?.params?.notShowLoading ||
    (err.config.params.notShowError != undefined && !err.config.params?.notShowError)
  );
}

// No HTTP response at all means request setup itself failed (e.g. an expired/missing token
// refresh threw). mapErrorToMessage turns that into the 401-aware title/message shown to the user.
function deriveMissingResponseErrorDetails(err): { errorTitle: string; errorMessage: string } {
  logAxiosError(err);
  const messageAndTitle = mapErrorToMessage(err);
  return { errorTitle: messageAndTitle.title, errorMessage: messageAndTitle.message };
}

function deriveIncompatibleVersionErrorDetails(err): { errorTitle: string; errorMessage: string } {
  const currentVersion = err.response.data.currentVersion;
  const requiredVersion = err.response.data.requiredVersion;
  return {
    errorTitle: formatMessage("cof.axios.error.title.incompatibleversion"),
    errorMessage: formatMessageWithParams("cof.axios.error.message.incompatibleversion", {
      currentVersion,
      requiredVersion,
    }),
  };
}

function deriveArrayBufferOrJsonErrorDetails(err): {
  severity: string;
  errorTitle: string;
  errorMessage: string;
  enableRefreshPanel: boolean;
} {
  /*Axios is trying to make an ArrayBuffer from the error object, so we need to convert it back to an object using TextDecoder and JSON.parse*/
  const decoder = new TextDecoder("utf-8");
  if (err.response.data instanceof ArrayBuffer) {
    const severity = JSON.parse(decoder.decode(err.response.data))[0].severity;
    const errorTitle = JSON.parse(decoder.decode(err.response.data))[0].error;
    const errorMessage = JSON.parse(decoder.decode(err.response.data))[0].description;
    return { severity, errorTitle, errorMessage, enableRefreshPanel: undefined };
  }
  const genericErrorsList = err.response.data;
  const severity = genericErrorsList && genericErrorsList.length > 0 ? genericErrorsList[0].severity : "error";
  const errorTitle =
    genericErrorsList && genericErrorsList.length > 0 ? tryFormatMessageWithJson(genericErrorsList[0].error) : "";
  const errorMessage =
    genericErrorsList && genericErrorsList.length > 0 ? tryFormatMessageWithJson(genericErrorsList[0].description) : "";
  const enableRefreshPanel = err.response.data.enableRefreshPanelOption;
  return { severity, errorTitle, errorMessage, enableRefreshPanel };
}

function deriveTooLargeErrorDetails(err): { errorTitle: string; errorMessage: string; enableRefreshPanel: boolean } {
  const genericErrorsList = err.response.data;
  const errorTitle = genericErrorsList && genericErrorsList.length > 0 ? genericErrorsList[0].error : "";
  const errorMessage = genericErrorsList && genericErrorsList.length > 0 ? genericErrorsList[0].description : "";
  const enableRefreshPanel = err.response.data.enableRefreshPanelOption;
  return { errorTitle, errorMessage, enableRefreshPanel };
}

function deriveNotFoundErrorDetails(err): { errorTitle: string; errorMessage: string } {
  const coServiceError = err.response.data.httpStatus;
  if (coServiceError) {
    return {
      errorTitle: tryFormatMessageWithJson(err.response.data.error),
      errorMessage: tryFormatMessageWithJson(err.response.data.description),
    };
  }
  return {
    errorTitle: err.response.data.status,
    errorMessage: formatMessage("cof.axios.error.serviceNotAvailable"),
  };
}

function deriveDefaultStatusErrorDetails(err): { errorTitle: string; errorMessage: string } {
  const errorTitle = err.response.status;
  let errorMessage = formatMessage("cof.axios.error.message.general");
  if (err.response.data && typeof err.response.data === "string") {
    errorMessage = err.response.data;
  } else if (err.response.data?.statusText) {
    errorMessage = err.response.data.statusText;
  }
  return { errorTitle, errorMessage };
}

async function handleResponseError(err) {
  let errorTitle = "";
  let errorMessage: string;
  let severity = "error";
  let enableRefreshPanel: boolean;
  let notifyError: boolean = getDefaultNotifyError(err);
  if (!err.config?.params?.notShowLoading) {
    LoadingManager.remove(API_CALL_KEY + err.config?.url);
  }
  if (!err.response || err.response.data.length === 0) {
    const details = deriveMissingResponseErrorDetails(err);
    errorTitle = details.errorTitle;
    errorMessage = details.errorMessage;
  } else if (err.response.status === 501) {
    const details = deriveIncompatibleVersionErrorDetails(err);
    errorTitle = details.errorTitle;
    errorMessage = details.errorMessage;
  } else if (err.response.status === 512) {
    const details = deriveArrayBufferOrJsonErrorDetails(err);
    severity = details.severity;
    errorTitle = details.errorTitle;
    errorMessage = details.errorMessage;
    enableRefreshPanel = details.enableRefreshPanel;
  } else if (err.response.status === 409) {
    errorMessage = err.response.data.description;
    enableRefreshPanel = err.response.data.enableRefreshPanelOption;
    notifyError = err.response.data.endUserException;
    err.message = tryFormatMessageWithJson(errorMessage);
    err.enableRefreshPanelOption = enableRefreshPanel;
  } else if (err.response.status === 413) {
    const details = deriveTooLargeErrorDetails(err);
    errorTitle = details.errorTitle;
    errorMessage = details.errorMessage;
    notifyError = false;
    enableRefreshPanel = details.enableRefreshPanel;
  } else if (err.response.status === 404) {
    const details = deriveNotFoundErrorDetails(err);
    errorTitle = details.errorTitle;
    errorMessage = details.errorMessage;
  } else if (err.response.status === 555) {
    notifyError = false;
    StandaloneModalManager.create({
      title: "iXBRL",
      message: formatMessage(err.response.data[0].description),
      type: err.response.data[0].error,
    });
  } else if (err.response.status === 503) {
    notifyError = false;
  } else {
    const details = deriveDefaultStatusErrorDetails(err);
    errorTitle = details.errorTitle;
    errorMessage = details.errorMessage;
  }
  if (notifyError) {
    NotificationManager.addNotification({
      title: errorTitle,
      message: errorMessage,
      key: API_CALL_NOTIFICATION,
      type: severity.toLowerCase() as "success" | "error" | "warning" | "info",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
      enableReloadPanel: enableRefreshPanel,
    });
  }
  return Promise.reject(err);
}

axiosInstance.interceptors.response.use((response) => {
  const params: AxiosCustomParams | undefined = response.config.params;
  if (!params?.notShowLoading) {
    LoadingManager.remove(API_CALL_KEY + response.config.url);
  }
  if (params?.invalidateCache) {
    params.invalidateCache.forEach((key) => coCache.clearKey(key));
  }
  return response;
}, handleResponseError);

// In-flight request deduplication for GET requests
const inflightRequests = new Map<string, AxiosPromise<unknown>>();
let _originalAdapter: AxiosAdapter | null = null;

interface CacheAwareRequestConfig extends InternalAxiosRequestConfig {
  /** Parsed cache directive retained when axios-retry rebuilds a config after the wire header was stripped. */
  coCacheTtl?: number;
}

function getOriginalAdapter(): AxiosAdapter {
  return _originalAdapter;
}

function getDedupKey(config: InternalAxiosRequestConfig): string | null {
  if (config.method?.toLowerCase() !== "get") return null;
  // `invalidateCache` is a client-only directive: exclude it from the key so a mutating call
  // that declares it does not produce a distinct key from the equivalent read (spec §2.3).
  let params = "";
  if (config.params) {
    const paramsForKey = { ...config.params };
    delete (paramsForKey as AxiosCustomParams).invalidateCache;
    params = JSON.stringify(paramsForKey);
  }
  // Include baseURL (per-tenant, set by the request interceptor) so cached GETs are never
  // shared across tenants/configurations that resolve the same relative url.
  const base = config.baseURL ?? "";
  return `${base}${config.url}|${params}`;
}

if (axiosInstance.defaults) {
  // Resolve the real adapter BEFORE overwriting — in Axios 1.x,
  // defaults.adapter is ['xhr','http','fetch'], not a callable function.
  _originalAdapter = getAdapter(axiosInstance.defaults.adapter);

  axiosInstance.defaults.adapter = function dedupAdapter(config: InternalAxiosRequestConfig): AxiosPromise {
    const dedupKey = getDedupKey(config);
    if (!dedupKey) {
      return getOriginalAdapter()(config);
    }

    const cacheConfig: CacheAwareRequestConfig = config;
    // Parse and strip the x-cache-ttl header before any network activity. Keep the parsed value
    // on the config itself: axios-retry rebuilds the request from this config after a network
    // error, by which point the synthetic wire header has already been stripped.
    const rawTtl = config.headers?.["x-cache-ttl"];
    // Spec §4: absent header -> default TTL; a non-numeric or negative value is treated as 0 (bypass cache).
    let ttl = cacheConfig.coCacheTtl;
    if (rawTtl !== undefined) {
      const parsed = Number(rawTtl);
      ttl = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
      cacheConfig.coCacheTtl = ttl;
    }
    const skipCache = ttl === 0;
    if (config.headers) {
      delete config.headers["x-cache-ttl"];
    }

    // Cache read: return immediately on hit (before checking inflight map)
    if (!skipCache) {
      const cached = coCache.get<AxiosResponse>(dedupKey);
      if (cached !== null) {
        console.debug("[CoCache]", { key: dedupKey, outcome: "HIT", ttlMs: ttl ?? 5000 });
        return Promise.resolve(cached);
      }
    }

    // Inflight dedup: reuse an in-progress request for the same key
    const existing = inflightRequests.get(dedupKey);
    if (existing !== undefined) {
      console.debug("[CoCache]", { key: dedupKey, outcome: "INFLIGHT", ttlMs: ttl ?? 5000 });
      return existing;
    }

    // MISS: not in cache and not in flight
    if (!skipCache) {
      console.debug("[CoCache]", { key: dedupKey, outcome: "MISS", ttlMs: ttl ?? 5000 });
    }

    const request = getOriginalAdapter()(config).then(
      (response) => {
        inflightRequests.delete(dedupKey);
        // Cache write: only on success and when caching is not bypassed
        if (!skipCache) {
          coCache.set(dedupKey, response, ttl);
        }
        return response;
      },
      (error: unknown) => {
        inflightRequests.delete(dedupKey);
        throw error;
      }
    ) as AxiosPromise<unknown>;

    inflightRequests.set(dedupKey, request);
    return request;
  };
}

// Exported for testing only
export function _getInflightRequestsForTesting(): Map<string, AxiosPromise<unknown>> {
  return inflightRequests;
}

export function _setAdapterForTesting(adapter: (config: InternalAxiosRequestConfig) => Promise<unknown>): void {
  _originalAdapter = adapter as AxiosAdapter;
}

export function _resetCacheForTesting(): void {
  coCache.clear();
}

// @ts-ignore
/**
 * we use generic http client not axios instance
 * the url is self-signed and we get thg binary
 * content of the response
 */
export async function getPublicResourceAsBase64Binary(url: string): Promise<string> {
  const response = await axios.get(url, {
    responseType: "arraybuffer",
  });
  if (response.status === 200) {
    return Buffer.from(response.data, "binary").toString("base64");
  } else {
    throw new Error("Error getting content: " + response.statusText + " (" + response.status + ")");
  }
}

type AuthErrorCauseCode = "NO_REFRESH_TOKEN" | "FAIL_TO_REFRESH_TOKEN" | "NO_TOKEN";

class AuthError extends Error {
  constructor(
    public cause: AuthErrorCauseCode,
    public code: string
  ) {
    super(cause);
  }
}

export function mapErrorToMessage(e: { code?: any; response?: any; message?: any }): {
  message: string;
  title: string;
} {
  let title = formatMessage("cof.axios.error.title.general");
  let message = formatMessage("cof.axios.error.message.general");
  if (e.code === "401" || e.response?.status === 401) {
    title = formatMessage("cof.axios.error.title.authorization");
    message = formatMessage("cof.axios.error.message.authorization.required");
  }
  if (e.message == "NO_REFRESH_TOKEN" || e.message == "FAIL_TO_REFRESH_TOKEN") {
    message = formatMessage("cof.axios.error.message.authorization.expired");
  }
  if (e.message == "NO_TOKEN") {
    message = formatMessage("cof.axios.error.message.authorization.required");
  }
  return { message, title };
}
